import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { WebOrderPayment } from '@orders'
import type { PaymentMethodsResponse } from '@adyen/adyen-web'
import { useConfigStore } from './configStore'  
import { useCustomerStore } from './customerStore'
import { isExpired } from '@/js/helpers/payment-methods'
import type { Address, IRogue } from '@/js/services/RogueTypes'
import type { SavedMethod, SavedPaymentMethod } from '@/js/stores/paymentMethodTypes'

export const usePaymentMethodStore = defineStore('paymentMethods', () => {
  let adyenPromise: Promise<void> | undefined

  const paymentMethodsResponse = ref<PaymentMethodsResponse>()
  const savedMethods = ref<SavedPaymentMethod[]>([])
  const availableOptions = ref<{key: string, name: string, icon: string, configuration?: object}[]>([])
  const loaded = ref(false)

  const adyenCardBrands = ref<string[]>([])

  const defaultCard = computed<SavedPaymentMethod>(() => savedMethods.value.find((pm: SavedPaymentMethod) => pm.isDefault && pm.adyenType === 'scheme'))

  const configStore = useConfigStore()
  const customerStore = useCustomerStore()

  function setPaymentMethods(methods: SavedMethod[]) {
    // @ts-ignore
    savedMethods.value = methods
      .filter(method => method.adyenType === 'scheme')
      .filter(method => method.storedPaymentMethodId !== undefined)
      .sort((a, b) => {
        if (a.isDefault) {
          return -1
        }
        if (b.isDefault) {
          return 1
        }

        return 0
      })

    savedMethods.value.filter((method: SavedPaymentMethod) => method.cardExpYear !== undefined && method.cardExpMonth !== undefined).forEach((card: SavedPaymentMethod) => {
      card.expired = isExpired(card.cardExpYear!, card.cardExpMonth!)
    })
  }

  async function loadPaymentMethods(rogue: IRogue, force = false): Promise<void> {
    if (!rogue.adyenCheckout) {
      return Promise.resolve()
    }

    if (!adyenPromise || force) {
      // Load the available and stored payment methods for this customer
      adyenPromise = rogue.fetch<PaymentMethodsResponse & { storedMethods: SavedMethod[] }>(`/svc_paymentmethod/methods?country=${rogue.l10n.country}&locale=${rogue.l10n.locale}`)
        .then(async response => {
          configStore.checkApplePay(rogue)
          configStore.checkGooglePay(rogue)
          response.paymentMethods = (response.paymentMethods || [])
          // Filter out unavailable types
            .filter(pm => pm.type !== 'applepay' || configStore.applePay)
            .filter(pm => pm.type !== 'googlepay' || configStore.googlePay)
            .filter(pm => pm.type !== 'paypal' || !rogue.appContext || rogue.isApp(<string>rogue.featureFlags.eval('app_paypal')))

          // @ts-ignore
          response.storedPaymentMethods?.forEach(pm => pm.storedPaymentMethodId = pm.storedPaymentMethodId || pm.id)

          // Sort any available types that have a list of banks
          response.paymentMethods.forEach(pm => {
            // @ts-ignore
            if (pm.issuers && pm.issuers.length) {
              // @ts-ignore
              pm.issuers = pm.issuers.sort((a, b) => a.name.localeCompare(b.name))
            }
          })

          paymentMethodsResponse.value = {
            paymentMethods       : response.paymentMethods,
            storedPaymentMethods : response.storedPaymentMethods,
          }

          // UI options include an icon
          availableOptions.value = (response.paymentMethods || [])
            .map(m => {
              const opts = {
                name          : m.type === 'scheme' ? rogue.__('Credit or Debit Card') : m.name,
                key           : m.type,
                icon          : `https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/small/${m.type === 'scheme' ? 'card' : m.type}@2x.png`,
                configuration : m.configuration,
              }
              if (opts.key === 'trustly') {
                opts.icon = 'https://assets.roguefitness.com/image/upload/UI/logos/icon_trustly.svg'
              } else if (opts.key === 'ideal') {
                opts.icon = 'https://assets.roguefitness.com/image/upload/UI/logos/iDEAL_Wero_Lockup_Yellow_Horizontal_RGB_q7dcf8.svg'
              } else if (opts.key === 'mobilepay') {
                opts.icon = 'https://assets.roguefitness.com/image/upload/UI/logos/MP_Logo_Blue.png'
              } else if (opts.key.includes('klarna')) {
                opts.icon = 'https://assets.roguefitness.com/image/upload/UI/logos/Klarna-badge_u0wjn1.png'
              }

              return opts
            })

          adyenCardBrands.value = response.paymentMethods.find(m => m.type === 'scheme')?.brands || []

          setPaymentMethods(response.storedMethods)
          loaded.value = true
        })
        .catch(e => {
          rogue.log.error('Error loading payment methods', e)
        })
    }

    return Promise.resolve(adyenPromise)
  }

  async function updatePaymentMethod(card: SavedPaymentMethod, rogue: IRogue): Promise<void> {
    if (!customerStore.loggedIn) {
      return
    }

    if (!card.billingAddressId && card.billingAddress) {
      if (!card.billingAddress._id) {
        card.billingAddress = (await customerStore.saveAddress(card.billingAddress, rogue)) as Address
      }
      card.billingAddressId = card.billingAddress._id
    }

    // If this is the default, we need to un-default the previous default
    const currentDefault = savedMethods.value.find((cc: SavedPaymentMethod) => cc.isDefault)

    if (card.isDefault && currentDefault && currentDefault.storedPaymentMethodId !== card.storedPaymentMethodId) {
      currentDefault.isDefault = false
      await rogue.fetch<any>('/svc_paymentmethod/methods', 'PUT', {
        paymentMethod: {
          storedPaymentMethodId: currentDefault.storedPaymentMethodId,
        },
        isDefault: currentDefault.isDefault,
      })
    }

    // Update existing card
    if (card.storedPaymentMethodId) {
      await rogue.fetch<any>('/svc_paymentmethod/methods', 'PUT', {
        paymentMethod: {
          storedPaymentMethodId: card.storedPaymentMethodId,
          // m2-paymentmethod will try to update adyen data if the following fields are included in paymentMethod
          // holderName            : card.holderName,
          // expiryMonth           : card.cardExpMonth,
          // expiryYear            : card.cardExpYear,
        },
        isDefault        : card.isDefault,
        billingAddressId : card.billingAddressId,
      })

      await loadPaymentMethods(rogue, true)
    }
  }

  async function deletePaymentMethod(paymentMethod: WebOrderPayment, rogue: IRogue): Promise<void> {
    if (!customerStore.loggedIn || !paymentMethod.storedPaymentMethodId) {
      return
    }

    const res = await rogue.fetch<string>(`/svc_paymentmethod/methods/${paymentMethod.storedPaymentMethodId}`, 'DELETE')
    if (res === 'ok') {
      setPaymentMethods([...savedMethods.value].filter(pm => pm.storedPaymentMethodId !== paymentMethod.storedPaymentMethodId))
      loadPaymentMethods(rogue, true)
    }
  }

  function $reset() {
    adyenPromise = undefined
    savedMethods.value = []
    availableOptions.value = []
    loaded.value = false
  }

  return {
    savedMethods,
    defaultCard,
    availableOptions,
    adyenCardBrands,
    loaded,
    paymentMethodsResponse,
    loadPaymentMethods,
    updatePaymentMethod,
    deletePaymentMethod,
    $reset,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePaymentMethodStore, import.meta.hot))
}